import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, Observable, tap, throwError } from 'rxjs';
import {
  ReconsRequestParams, ReconsResp,
  ReconSyncReq, ReconSyncResp,
  ReconMatchReq, ReconMatchResp,
  ReconUnmatchedListResp,
  BankMandiriReportResp
} from "../../interfaces/recons.interface";
import { toQueryString } from '../../shared/helpers/helpers';


@Injectable({
  providedIn: 'root',
})
export class ReconsService {
  private apiUrl = environment.api;

  constructor(private http: HttpClient) {}

  private handleResponse<T>(response: ReconsResp): ReconsResp {
    if (response?.code !== 0) {
      throw new Error(response.message);
    }
    return response;
  }

  private handleError(error: any): Observable<never> {
    console.error('API Error:', error);
    return throwError(() => new Error(error.error?.message || 'Error'));
  }

  getProviderRecons(reqData: ReconsRequestParams): Observable<ReconsResp> {
    const queryString = {
      pageNo: reqData.pageNo,
      rowPerPage: reqData.rowPerPage,
    };
    const reconUrl = this.apiUrl + '/provider-recons';
    const url = reconUrl + '?' + toQueryString(queryString);
    return this.http.post<ReconsResp>(url, reqData).pipe(
      tap((response) => this.handleResponse(response)),
      catchError((error) => this.handleError(error))
    );
  }

  searchRecons(reqData: ReconsRequestParams): Observable<ReconsResp> {
    const reconUrl = this.apiUrl + '/recons';
    const url = reconUrl + '?' + toQueryString(reqData);
    return this.http.get<ReconsResp>(url).pipe(
      tap((response) => this.handleResponse(response)),
      catchError((error) => this.handleError(error))
    );
  }

  /**
   * Kick off an async PDAM data refresh ("Tarik Data" button).
   * Returns immediately with the job descriptor; the caller should poll
   * getSyncStatus(job_id) every few seconds until status becomes
   * "completed" or "failed".
   */
  triggerSync(reqData: ReconSyncReq): Observable<ReconSyncResp> {
    const url = this.apiUrl + '/recons/sync';
    return this.http.post<ReconSyncResp>(url, reqData).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  /** Read the current state of an in-flight sync job. */
  getSyncStatus(jobId: string): Observable<ReconSyncResp> {
    const url = this.apiUrl + '/recons/sync/' + encodeURIComponent(jobId);
    return this.http.get<ReconSyncResp>(url).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  /**
   * Click handler for "Match dengan Transaksi". Synchronous on BE side
   * — admin gets the matched_count back immediately. Re-running the
   * same (code, date) wipes prior matches before recomputing.
   */
  runMatch(reqData: ReconMatchReq): Observable<ReconMatchResp> {
    const url = this.apiUrl + '/recons/match';
    return this.http.post<ReconMatchResp>(url, reqData).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  /**
   * Feeds the "Unmatched Transactions" tab — list of successful tx the
   * matcher could not pair to any recon row for the requested day.
   */
  getUnmatched(code: string, date: string, pageNo = 1, rowPerPage = 25): Observable<ReconUnmatchedListResp> {
    const qs = `code=${encodeURIComponent(code)}&date=${encodeURIComponent(date)}&pageNo=${pageNo}&rowPerPage=${rowPerPage}`;
    const url = this.apiUrl + '/recons/unmatched?' + qs;
    return this.http.get<ReconUnmatchedListResp>(url).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  /**
   * Bank Mandiri reconciliation report — drives the FE "VA Bank Mandiri"
   * and "PPOB Bank Mandiri" Excel download buttons. Response carries
   * the all_matched gate flag plus two pre-classified row slices.
   */
  getBankMandiriReport(code: string, date: string): Observable<BankMandiriReportResp> {
    const qs = `code=${encodeURIComponent(code)}&date=${encodeURIComponent(date)}`;
    const url = this.apiUrl + '/recons/bank-mandiri-report?' + qs;
    return this.http.get<BankMandiriReportResp>(url).pipe(
      catchError((error) => this.handleError(error))
    );
  }
}
